import {
  DownloadOutlined,
  HistoryOutlined,
  ReportProblemOutlined,
  InventoryOutlined,
  LocalShippingOutlined,
  LocationOnOutlined,
  SearchOutlined,
  VisibilityOutlined,
  WarehouseOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  StockUnit,
  StockMovement,
  StockRemediation,
  StockItemSummary,
  StockLocationSummary,
  StockWarehouseSummary,
  correctStockLotTraceability,
  correctStockSerialNumber,
  correctStockRotationDate,
  isAuthenticationError,
  loadStockByItem,
  loadStockByLocation,
  loadStockByWarehouse,
  loadAllStockUnits,
  loadStockMovements,
  loadStockRemediations,
  loadStockUnitsByScope,
  loadStockUnits
} from "./api";
import { login } from "./auth";
import { ListRowActions } from "./ListRowActions";
import { matchesListSyntax } from "./listSearch";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { csv, downloadZip } from "./csvExport";

function quantity(value: number, locale = "fr-FR") {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 6
  }).format(value);
}

export function StockPage({
  onBack,
  permissions,
  initialSearch = "",
  initialView = "item"
}: {
  onBack: () => void;
  permissions: string[];
  initialSearch?: string;
  initialView?: "item" | "warehouse" | "location" | "hu" | "remediation" | "movement";
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const [search, setSearch] = useState(initialSearch);
  const [selected, setSelected] = useState<StockItemSummary | null>(null);
  const [view, setView] =
    useState<"item" | "warehouse" | "location" | "hu" | "remediation" | "movement">(initialView);
  const [selectedHu, setSelectedHu] = useState<StockUnit[] | null>(null);
  const [movementUnit, setMovementUnit] = useState<StockUnit | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<StockWarehouseSummary | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<StockLocationSummary | null>(null);
  const [mobileSelection, setMobileSelection] = useState<
    | { kind: "item"; value: StockItemSummary }
    | { kind: "warehouse"; value: StockWarehouseSummary }
    | { kind: "location"; value: StockLocationSummary }
    | { kind: "hu"; value: StockUnit[] }
    | null
  >(null);
  const stock = useQuery({
    queryKey: ["stock-by-item"],
    queryFn: loadStockByItem
  });
  const warehouses = useQuery({
    queryKey: ["stock-by-warehouse"],
    queryFn: loadStockByWarehouse
  });
  const locations = useQuery({
    queryKey: ["stock-by-location"],
    queryFn: loadStockByLocation
  });
  const movements = useQuery({
    queryKey: ["stock-movements"],
    queryFn: () => loadStockMovements()
  });
  const allUnits = useQuery({
    queryKey: ["stock-units-all"],
    queryFn: loadAllStockUnits
  });
  const remediations = useQuery({
    queryKey: ["stock-remediations"],
    queryFn: loadStockRemediations
  });
  useEffect(() => {
    setSearch(initialSearch);
    setView(initialView);
  }, [initialSearch, initialView]);
  const filteredItems = useMemo(() => (stock.data ?? []).filter((item) =>
    !search.trim() || matchesListSyntax({
      id: `${item.itemId} ${item.itemCode}`,
      libelle: item.itemName,
      uom: item.uom,
      total: item.totalQuantity,
      disponible: item.availableQuantity,
      quarantaine: item.quarantinedQuantity,
      rejete: item.rejectedQuantity,
      hu: item.handlingUnitCount
    }, search)
  ), [search, stock.data]);
  const filteredWarehouses = useMemo(() => (warehouses.data ?? []).filter(
    (entry) => !search.trim() || matchesListSyntax({
      entrepot: `${entry.warehouseCode} ${entry.warehouseName}`,
      client: `${entry.clientCode ?? ""} ${entry.clientName ?? t("stock.ownStock")}`,
      uom: entry.uom,
      total: entry.totalQuantity,
      disponible: entry.availableQuantity,
      quarantaine: entry.quarantinedQuantity,
      rejete: entry.rejectedQuantity,
      article: entry.itemCount,
      hu: entry.handlingUnitCount,
      emplacement: entry.locationCount
    }, search)
  ), [search, warehouses.data]);
  const filteredLocations = useMemo(() => (locations.data ?? []).filter(
    (entry) => !search.trim() || matchesListSyntax({
      entrepot: `${entry.warehouseCode} ${entry.warehouseName}`,
      emplacement: `${entry.locationCode} ${entry.locationName}`,
      type: entry.locationType,
      zone: `${entry.areaCode ?? ""} ${entry.areaName ?? ""}`,
      section: `${entry.sectionCode ?? ""} ${entry.sectionName ?? ""}`,
      client: `${entry.clientCode ?? ""} ${entry.clientName ?? t("stock.ownStock")}`,
      uom: entry.uom,
      total: entry.totalQuantity,
      disponible: entry.availableQuantity,
      quarantaine: entry.quarantinedQuantity,
      rejete: entry.rejectedQuantity,
      article: entry.itemCount,
      hu: entry.handlingUnitCount
    }, search)
  ), [locations.data, search]);
  const filteredMovements = useMemo(() => (movements.data ?? []).filter(
    (movement) => !search.trim() || matchesListSyntax({
      article: `${movement.itemCode} ${movement.itemName}`,
      hu: movement.handlingUnitNumber,
      entrepot: movement.warehouseCode,
      type: movement.transactionType,
      origine: `${movement.sourceCode ?? ""} ${movement.sourceName ?? ""}`,
      destination: `${movement.destinationCode ?? ""} ${movement.destinationName ?? ""}`,
      reference: movement.sourceReference,
      qualite: `${movement.previousQualityStatus ?? ""} ${movement.qualityStatus}`,
      statut: `${movement.previousStockStatus ?? ""} ${movement.stockStatus ?? ""}`,
      conditionnement: `${movement.previousPackagingCode ?? ""} ${movement.targetPackagingCode ?? ""}`,
      commentaire: movement.comment,
      user: `${movement.userDisplayName ?? ""} ${movement.userEmail ?? ""}`,
      date: new Date(movement.occurredAt).toLocaleString(locale)
    }, search)
  ), [movements.data, search]);
  const huGroups = useMemo(() => {
    const groups = new Map<string, StockUnit[]>();
    for (const unit of allUnits.data ?? []) {
      groups.set(unit.handlingUnitId, [
        ...(groups.get(unit.handlingUnitId) ?? []), unit
      ]);
    }
    return [...groups.values()].filter((units) => {
      const unit = units[0];
      return !search.trim() || matchesListSyntax({
        hu: unit.handlingUnitNumber,
        article: units.map((entry) =>
          `${entry.itemCode} ${entry.itemName}`).join(" "),
        client: `${unit.clientCode ?? ""} ${unit.clientName ?? t("stock.ownStock")}`,
        entrepot: unit.warehouseCode,
        emplacement: `${unit.locationCode} ${unit.locationName}`,
        lot: units.map((entry) => entry.lotNumber ?? "").join(" "),
        serie: units.map((entry) => entry.serialNumber ?? "").join(" "),
        qualite: units.map((entry) => entry.qualityStatus).join(" "),
        statut: `${unit.stockStatus} ${unit.handlingUnitStatus}`
      }, search);
    });
  }, [allUnits.data, search]);
  const filteredRemediations = useMemo(() => (remediations.data ?? []).filter(
    (entry) => !search.trim() || matchesListSyntax({
      hu: entry.handlingUnitNumber,
      article: entry.itemSummary ?? "",
      raison: remediationReason(entry, t),
      entrepot: entry.warehouseCode,
      client: `${entry.clientCode ?? ""} ${entry.clientName ?? ""}`,
      attendu: entry.expectedLocationCode,
      emplacement: entry.currentLocationCode ?? "",
      inventaire: entry.campaignNumber,
      action: entry.actionNumber,
      statut: entry.status
    }, search)
  ), [remediations.data, search, t]);
  const totalReferences = stock.data?.length ?? 0;
  const totalHandlingUnits = stock.data?.reduce(
    (total, item) => total + item.handlingUnitCount, 0
  ) ?? 0;
  const blockedReferences = stock.data?.filter(
    (item) => item.nonUsableQuantity > 0
  ).length ?? 0;
  const stockTotals = useMemo(() => {
    const totals = new Map<string, {
      total: number; available: number; quarantined: number; rejected: number
    }>();
    for (const item of stock.data ?? []) {
      const value = totals.get(item.uom)
        ?? { total: 0, available: 0, quarantined: 0, rejected: 0 };
      value.total += item.totalQuantity;
      value.available += item.availableQuantity;
      value.quarantined += item.quarantinedQuantity;
      value.rejected += item.rejectedQuantity;
      totals.set(item.uom, value);
    }
    return totals;
  }, [stock.data]);
  const totalLabel = (
    field: "total" | "available" | "quarantined" | "rejected"
  ) => [...stockTotals].map(([uom, value]) =>
    `${quantity(value[field], locale)} ${uom}`).join(" · ") || "0";
  useEffect(() => setMobileSelection(null), [view]);
  const executeMobilePrimary = () => {
    if (!mobileSelection) return;
    if (mobileSelection.kind === "item") setSelected(mobileSelection.value);
    if (mobileSelection.kind === "warehouse") setSelectedWarehouse(mobileSelection.value);
    if (mobileSelection.kind === "location") setSelectedLocation(mobileSelection.value);
    if (mobileSelection.kind === "hu") setSelectedHu(mobileSelection.value);
  };
  const statusTabs = [
    { value: "item" as const, label: t("stock.tabs.item"), mobileLabel: t("stock.tabs.itemMobile"),
      count: filteredItems.length },
    { value: "warehouse" as const, label: t("stock.tabs.warehouse"), mobileLabel: t("stock.tabs.warehouseMobile"),
      count: filteredWarehouses.length },
    { value: "location" as const, label: t("stock.tabs.location"), mobileLabel: t("stock.tabs.locationMobile"),
      count: filteredLocations.length },
    { value: "hu" as const, label: t("stock.tabs.hu"), mobileLabel: "HU",
      count: huGroups.length },
    { value: "remediation" as const, label: t("stock.tabs.remediation"),
      mobileLabel: t("stock.tabs.remediationMobile"), count: filteredRemediations.length },
    { value: "movement" as const, label: t("stock.tabs.movement"), mobileLabel: t("stock.tabs.movementMobile"),
      count: filteredMovements.length }
  ];
  const exportLoading = stock.isLoading || warehouses.isLoading
    || locations.isLoading || allUnits.isLoading || remediations.isLoading;
  const exportStock = () => {
    const header = (keys: string[]) => keys.map((key) =>
      t(`stock.export.headers.${key}`));
    const files = [
      {
        name: "material.csv",
        content: csv([
          header(["materialCode", "materialName", "uom", "total", "available",
            "quarantine", "rejected", "nonUsable", "huCount", "warehouseCount",
            "lotCount", "serialCount"]),
          ...(stock.data ?? []).map((item) => [item.itemCode, item.itemName, item.uom,
            item.totalQuantity, item.availableQuantity, item.quarantinedQuantity,
            item.rejectedQuantity, item.nonUsableQuantity, item.handlingUnitCount,
            item.warehouseCount, item.lotCount, item.serialCount])
        ])
      },
      {
        name: "warehouse.csv",
        content: csv([
          header(["warehouseCode", "warehouseName", "clientCode", "clientName", "uom",
            "total", "available", "quarantine", "rejected", "nonUsable",
            "materialCount", "huCount", "locationCount"]),
          ...(warehouses.data ?? []).map((entry) => [entry.warehouseCode,
            entry.warehouseName, entry.clientCode, entry.clientName, entry.uom,
            entry.totalQuantity, entry.availableQuantity, entry.quarantinedQuantity,
            entry.rejectedQuantity, entry.nonUsableQuantity, entry.itemCount,
            entry.handlingUnitCount, entry.locationCount])
        ])
      },
      {
        name: "location.csv",
        content: csv([
          header(["warehouseCode", "warehouseName", "locationCode", "locationName",
            "locationType", "areaCode", "areaName", "sectionCode", "sectionName",
            "clientCode", "clientName", "uom", "total", "available", "quarantine",
            "rejected", "nonUsable", "materialCount", "huCount"]),
          ...(locations.data ?? []).map((entry) => [entry.warehouseCode,
            entry.warehouseName, entry.locationCode, entry.locationName,
            entry.locationType, entry.areaCode, entry.areaName, entry.sectionCode,
            entry.sectionName, entry.clientCode, entry.clientName, entry.uom,
            entry.totalQuantity, entry.availableQuantity, entry.quarantinedQuantity,
            entry.rejectedQuantity, entry.nonUsableQuantity, entry.itemCount,
            entry.handlingUnitCount])
        ])
      },
      {
        name: "handling-unit.csv",
        content: csv([
          header(["huNumber", "materialCode", "materialName", "quantity", "uom",
            "qualityStatus", "stockStatus", "huStatus", "flowStage", "warehouseCode",
            "locationCode", "locationName", "areaCode", "sectionCode", "clientCode",
            "clientName", "lotNumber", "expirationDate", "serialNumber",
            "packagingCode", "receiptNumber", "rotationPolicy", "rotationDate",
            "updatedAt"]),
          ...(allUnits.data ?? []).map((unit) => [unit.handlingUnitNumber,
            unit.itemCode, unit.itemName, unit.quantity, unit.uom, unit.qualityStatus,
            unit.stockStatus, unit.handlingUnitStatus, unit.flowStage,
            unit.warehouseCode, unit.locationCode, unit.locationName, unit.areaCode,
            unit.sectionCode, unit.clientCode, unit.clientName, unit.lotNumber,
            unit.expirationDate, unit.serialNumber, unit.packagingCode,
            unit.receiptNumber, unit.rotationPolicy, unit.rotationDate, unit.updatedAt])
        ])
      },
      {
        name: "remediation.csv",
        content: csv([
          header(["actionNumber", "campaignNumber", "investigationType", "status",
            "huNumber", "materialSummary", "warehouseCode", "clientCode", "clientName",
            "expectedLocation", "currentLocation", "reason", "createdAt", "assignedTo"]),
          ...(remediations.data ?? []).map((entry) => [entry.actionNumber,
            entry.campaignNumber, entry.investigationType, entry.status,
            entry.handlingUnitNumber, entry.itemSummary, entry.warehouseCode,
            entry.clientCode, entry.clientName, entry.expectedLocationCode,
            entry.currentLocationCode, entry.reason, entry.createdAt, entry.assignedTo])
        ])
      }
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadZip(`obviowms-stock-${stamp}.zip`, files);
  };
  const exportButton = (
    <Button variant="outlined" startIcon={<DownloadOutlined />}
      disabled={exportLoading} onClick={exportStock}>
      {t("stock.export.action")}
    </Button>
  );

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("stock.module")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("stock.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ display: { xs: "flex", sm: "none" }, justifyContent: "flex-end" }}>
        {exportButton}
      </Box>
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("stock.title")} compact onBack={onBack}
          actions={exportButton} />
      </Box>

      {(stock.isError || warehouses.isError || locations.isError
        || movements.isError || allUnits.isError || remediations.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={isAuthenticationError(stock.error)
            ? <Button color="inherit" size="small"
                onClick={() => void login()}>{t("stock.reconnect")}</Button>
            : <Button color="inherit" size="small"
                onClick={() => void Promise.all([
                  stock.refetch(), warehouses.refetch(), locations.refetch(),
                  movements.refetch(), allUnits.refetch(), remediations.refetch()
                ])}>{t("stock.retry")}</Button>}>
          {t("stock.loadError")}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5}
        sx={{ display: { xs: "none", sm: "flex" } }}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={800}>
              {totalLabel("total")}
            </Typography>
            <Typography color="text.secondary">
              {t("stock.metrics.physical", { count: totalReferences })}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={800}>
              {totalLabel("available")}
            </Typography>
            <Typography color="text.secondary">
              {t("stock.metrics.available", { count: totalHandlingUnits })}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={800}
              color={totalLabel("quarantined") !== "0"
                ? "warning.main" : "success.main"}>
              {totalLabel("quarantined")}
            </Typography>
            <Typography color="text.secondary">{t("stock.metrics.quarantine")}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={800}
              color={blockedReferences > 0 ? "error.main" : "success.main"}>
              {totalLabel("rejected")}
            </Typography>
            <Typography color="text.secondary">
              {t("stock.metrics.rejected", { count: blockedReferences })}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <ResponsiveStatusTabs value={view} items={statusTabs}
        onChange={(value) => {
          setView(value);
          setSearch("");
        }} />

      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <TextField fullWidth size="small" label={t("stock.search")}
            placeholder={view === "item"
              ? t("stock.searchExamples.item")
              : view === "warehouse"
                ? t("stock.searchExamples.warehouse")
                : view === "location"
                  ? t("stock.searchExamples.location")
                  : view === "hu"
                    ? t("stock.searchExamples.hu")
                  : view === "remediation"
                    ? t("stock.searchExamples.remediation")
                  : t("stock.searchExamples.movement")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
        </CardContent>
      </Card>

      <Box sx={{
        minHeight: 0,
        flex: { xs: "1 1 0", sm: "none", md: 1 },
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5, pb: { xs: 11, md: 0 }
      }}>
      {!stock.isLoading && !stock.isError && stock.data?.length === 0 && (
        <Alert severity="info">
          {t("stock.empty")}
        </Alert>
      )}
      {view === "item" && (stock.data?.length ?? 0) > 0
        && filteredItems.length === 0 && (
        <Alert severity="info">
          {t("stock.noSearchResult")}
        </Alert>
      )}

      {view === "item" && <Stack spacing={1.5}>
        {filteredItems.map((item) => (
          <Card key={`${item.itemId}-${item.uom}`} variant="outlined"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button")) return;
              setMobileSelection({ kind: "item", value: item });
            }} sx={{ cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelection?.kind === "item"
                && mobileSelection.value.itemId === item.itemId
                && mobileSelection.value.uom === item.uom
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main" }}>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ md: "center" }}>
                <InventoryOutlined color="secondary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {item.itemCode} · {item.itemName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("stock.total", { quantity: quantity(item.totalQuantity, locale), uom: item.uom })}
                    {" · "}{item.handlingUnitCount} HU
                    {" · "}{t("stock.counts.warehouses", { count: item.warehouseCount })}
                    {" · "}{t("stock.counts.lots", { count: item.lotCount })}
                    {item.serialCount > 0
                      ? ` · ${t("stock.counts.serials", { count: item.serialCount })}` : ""}
                  </Typography>
                </Box>
                <ListRowActions actions={
                  <Button variant="outlined" startIcon={<VisibilityOutlined />}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                    onClick={() => setSelected(item)}>
                    {t("stock.consult")}
                  </Button>
                } status={quantityChips(item, t, locale)} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>}
      {view === "warehouse" && (
        <StockWarehouseList entries={filteredWarehouses}
          onSelect={setSelectedWarehouse}
          onMobileSelect={(value) => setMobileSelection({ kind: "warehouse", value })}
          selected={mobileSelection?.kind === "warehouse"
            ? mobileSelection.value : null} />
      )}
      {view === "location" && (
        <StockLocationList entries={filteredLocations}
          units={allUnits.data ?? []} onSelect={setSelectedLocation}
          onMobileSelect={(value) => setMobileSelection({ kind: "location", value })}
          selected={mobileSelection?.kind === "location"
            ? mobileSelection.value : null} />
      )}
      {view === "hu" && (
        <StockHuList groups={huGroups} onSelect={setSelectedHu}
          onMobileSelect={(value) => setMobileSelection({ kind: "hu", value })}
          selectedId={mobileSelection?.kind === "hu"
            ? mobileSelection.value[0]?.handlingUnitId ?? null : null} />
      )}
      {view === "remediation" && (
        <StockRemediationList entries={filteredRemediations} />
      )}
      {view === "movement" && (
        <StockMovementList entries={filteredMovements} />
      )}
      </Box>

      <MobileActionBar primaryLabel={mobileSelection
        ? t("stock.consult") : t("stock.selectStock")}
        primaryDisabled={!mobileSelection}
        onPrimary={executeMobilePrimary} />

      <StockDetail item={selected} onClose={() => setSelected(null)}
        onShowMovements={setMovementUnit}
        canCorrectIdentity={permissions.includes("inventory:adjust")}
        canCorrectRotation={permissions.includes(
          "inventory:correct_rotation_date"
        )} />
      <ScopedStockDetail warehouse={selectedWarehouse}
        location={selectedLocation}
        onShowMovements={setMovementUnit}
        onClose={() => {
          setSelectedWarehouse(null);
          setSelectedLocation(null);
        }} />
      <StockMovementDialog unit={movementUnit}
        onClose={() => setMovementUnit(null)} />
      <StockHuDialog units={selectedHu}
        onShowMovements={setMovementUnit}
        onClose={() => setSelectedHu(null)} />
    </Stack>
  );
}

function remediationReason(entry: StockRemediation, t: TFunction) {
  if (entry.investigationType === "UNKNOWN_HU") return t("stock.remediation.unknownHu");
  if (entry.currentLocationCode
      && entry.currentLocationCode !== entry.expectedLocationCode) {
    return t("stock.remediation.wrongLocation");
  }
  return t("stock.remediation.notFound");
}

function StockRemediationList({ entries }: { entries: StockRemediation[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) {
    return <Alert severity="success">{t("stock.remediation.none")}</Alert>;
  }
  return <Stack spacing={1.5}>{entries.map((entry) => {
    const reason = remediationReason(entry, t);
    return <Card key={entry.investigationId} variant="outlined"
      sx={{ borderLeft: 4, borderLeftColor: "warning.main" }}>
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}
          alignItems={{ md: "center" }}>
          <ReportProblemOutlined color="warning" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={800}>
              {entry.handlingUnitNumber} · {reason}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {entry.itemSummary ?? t("stock.remediation.unidentified")}
              {" · "}{entry.warehouseCode}
              {entry.clientCode ? ` · ${entry.clientCode} · ${entry.clientName}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("stock.remediation.expected", { location: entry.expectedLocationCode })}
              {entry.currentLocationCode
                ? ` · ${t("stock.remediation.known", { location: entry.currentLocationCode })}`
                : ` · ${t("stock.remediation.noCurrent")}`}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" variant="outlined" label={entry.campaignNumber} />
              {entry.actionNumber &&
                <Chip size="small" variant="outlined" label={entry.actionNumber} />}
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                {t("stock.remediation.reason", { reason: entry.reason })}
              </Typography>
            </Stack>
          </Box>
          <Chip color="warning" label={entry.status === "IN_PROGRESS"
            ? t("stock.remediation.inProgress")
            : entry.status === "DETECTED" ? t("stock.remediation.detected") : t("stock.remediation.todo")} />
        </Stack>
      </CardContent>
    </Card>;
  })}</Stack>;
}

function quantityChips(entry: {
  availableQuantity: number;
  quarantinedQuantity: number;
  rejectedQuantity: number;
}, t: TFunction, locale = "fr-FR") {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
      justifyContent="flex-end">
      <Chip size="small" color="success"
        label={t("stock.quantity.available", { quantity: quantity(entry.availableQuantity, locale) })} />
      {entry.quarantinedQuantity > 0 && (
        <Chip size="small" color="warning"
          label={t("stock.quantity.quarantine", { quantity: quantity(entry.quarantinedQuantity, locale) })} />
      )}
      {entry.rejectedQuantity > 0 && (
        <Chip size="small" color="error"
          label={t("stock.quantity.rejected", { quantity: quantity(entry.rejectedQuantity, locale) })} />
      )}
    </Stack>
  );
}

function ownerLabel(clientCode: string | null, clientName: string | null, t: TFunction) {
  return clientCode ? `${clientCode} · ${clientName}` : t("stock.ownStock");
}

function flowLabel(unit: StockUnit, t: TFunction) {
  if (unit.flowStage === "IN_STOCK") return t("stock.flow.inStock");
  if (unit.flowStage === "EXPECTED") return t("stock.flow.expected");
  if (unit.flowStage === "DEPARTURE") return t("stock.flow.departure");
  if (unit.flowStage === "IN_TRANSIT") return t("stock.flow.transit");
  return t(unit.flowDirection === "OUTBOUND" ? "stock.flow.checkOut" : "stock.flow.checkIn");
}

function qualityLabel(status: StockUnit["qualityStatus"], t: TFunction) {
  if (status === "AVAILABLE") return t("stock.quality.available");
  if (status === "QUARANTINED") return t("stock.quality.quarantined");
  return t("stock.quality.rejected");
}

function movementLabel(type: StockMovement["transactionType"], t: TFunction) {
  if (type === "RECEIPT_POSTING") return t("stock.movement.receipt");
  if (type === "QUALITY_STATUS_CHANGE") return t("stock.movement.quality");
  if (type === "REPACKAGING") return t("stock.movement.repackaging");
  if (type === "IDENTITY_CORRECTION") return t("stock.movement.identity");
  if (type === "PICKING") return t("stock.movement.picking");
  if (type === "PICKING_REVERSAL") return t("stock.movement.pickingReversal");
  return t("stock.movement.putaway");
}

function StockMovementList({ entries }: { entries: StockMovement[] }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  if (entries.length === 0) {
    return <Alert severity="info">{t("stock.movement.none")}</Alert>;
  }
  return (
    <Stack spacing={1.5}>
      {entries.map((movement) => (
        <Card key={movement.transactionLineId} variant="outlined">
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}
              alignItems={{ md: "center" }}>
              <HistoryOutlined color="secondary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={800}>
                  {movement.handlingUnitNumber} · {movement.itemCode}
                  {" · "}{movementLabel(movement.transactionType, t)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {movement.sourceCode ?? "—"} →{" "}
                  {movement.destinationCode ?? "—"}
                  {" · "}{quantity(movement.quantity)} {movement.uom}
                  {" · "}{movement.warehouseCode}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(movement.occurredAt).toLocaleString(locale)}
                  {" · "}{movement.userDisplayName ?? t("stock.movement.unknownUser")}
                  {movement.sourceReference
                    ? ` · ${t("stock.movement.reference", { reference: movement.sourceReference })}` : ""}
                </Typography>
                {movement.previousQualityStatus
                  && movement.previousQualityStatus !== movement.qualityStatus && (
                  <Typography variant="caption" display="block"
                    color="text.secondary">
                    {t("stock.movement.qualityChange", {
                      from: movement.previousQualityStatus,
                      to: movement.qualityStatus
                    })}
                  </Typography>
                )}
                {movement.previousStockStatus
                  && movement.previousStockStatus !== movement.stockStatus && (
                  <Typography variant="caption" display="block"
                    color="text.secondary">
                    {t("stock.movement.stock")} {movement.previousStockStatus}
                    {" → "}{movement.stockStatus}
                  </Typography>
                )}
                {movement.previousQuantity != null
                  && movement.targetQuantity != null && (
                  <Typography variant="caption" display="block"
                    color="text.secondary">
                    {t("stock.movement.quantityChange", {
                      from: quantity(movement.previousQuantity),
                      to: quantity(movement.targetQuantity),
                      uom: movement.uom,
                      defaultValue: locale === "fr-FR"
                        ? "Quantité {{from}} {{uom}} → {{to}} {{uom}}"
                        : "Quantity {{from}} {{uom}} → {{to}} {{uom}}"
                    })}
                    {["PICKING", "PICKING_REVERSAL"].includes(movement.transactionType)
                      ? ` · ${t("stock.movement.pickedQuantity", {
                        quantity: quantity(movement.quantity), uom: movement.uom,
                        defaultValue: locale === "fr-FR"
                          ? "prélèvement {{quantity}} {{uom}}"
                          : "picked {{quantity}} {{uom}}"
                      })}` : ""}
                  </Typography>
                )}
                {movement.previousPackagingCode && (
                  <Typography variant="caption" display="block"
                    color="text.secondary">
                    {t("stock.movement.packaging")} {movement.previousPackagingCode} →{" "}
                    {movement.targetPackagingCode ?? "—"}
                  </Typography>
                )}
                {movement.identityField && (
                  <Typography variant="caption" display="block"
                    color="text.secondary">
                    {movement.identityField === "SERIAL_NUMBER"
                      ? t("stock.movement.serial")
                      : movement.identityField === "LOT_NUMBER"
                        ? t("stock.movement.lot")
                        : movement.identityField === "EXPIRATION_DATE"
                          ? t("stock.movement.expiration") : movement.identityField}
                    {" "}{movement.previousIdentityValue ?? "—"} →{" "}
                    {movement.targetIdentityValue ?? "—"}
                  </Typography>
                )}
                {movement.comment && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("stock.movement.comment", { comment: movement.comment })}
                  </Typography>
                )}
              </Box>
              <ListRowActions status={
                <Chip label={movement.transactionType === "PUTAWAY"
                  ? t("stock.movement.tags.putaway")
                  : movement.transactionType === "QUALITY_STATUS_CHANGE"
                    ? t("stock.movement.tags.quality")
                    : movement.transactionType === "REPACKAGING"
                      ? t("stock.movement.tags.repackaging")
                      : movement.transactionType === "IDENTITY_CORRECTION"
                        ? t("stock.movement.tags.correction")
                        : movement.transactionType === "PICKING"
                          ? t("stock.movement.tags.picking")
                          : movement.transactionType === "PICKING_REVERSAL"
                            ? t("stock.movement.tags.pickingReversal") : t("stock.movement.tags.receipt")}
                  color={movement.transactionType === "PUTAWAY"
                    ? "success"
                    : movement.transactionType === "QUALITY_STATUS_CHANGE"
                      ? "warning"
                      : movement.transactionType === "REPACKAGING"
                        ? "secondary"
                        : movement.transactionType === "IDENTITY_CORRECTION"
                          ? "warning" : movement.transactionType === "PICKING"
                            ? "secondary" : movement.transactionType === "PICKING_REVERSAL"
                              ? "warning" : "info"} />
              } />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function StockMovementDialog({
  unit,
  onClose
}: {
  unit: StockUnit | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const movements = useQuery({
    queryKey: ["stock-movements-unit", unit?.inventoryUnitId],
    queryFn: () => loadStockMovements({
      inventoryUnitId: unit!.inventoryUnitId
    }),
    enabled: Boolean(unit)
  });
  return (
    <Dialog open={Boolean(unit)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("stock.movement.history", { hu: unit?.handlingUnitNumber, item: unit?.itemCode })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {movements.isError && (
            <Alert severity="error">{t("stock.movement.loadError")}</Alert>
          )}
          {!movements.isLoading && !movements.isError
            && movements.data?.length === 0 && (
            <Alert severity="info">{t("stock.movement.empty")}</Alert>
          )}
          <StockMovementList entries={movements.data ?? []} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("stock.close")}</Button></DialogActions>
    </Dialog>
  );
}

function StockWarehouseList({
  entries,
  onSelect,
  onMobileSelect,
  selected
}: {
  entries: StockWarehouseSummary[];
  onSelect: (entry: StockWarehouseSummary) => void;
  onMobileSelect: (entry: StockWarehouseSummary) => void;
  selected: StockWarehouseSummary | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  if (entries.length === 0) {
    return <Alert severity="info">{t("stock.warehouse.none")}</Alert>;
  }
  return (
    <Stack spacing={1.5}>
      {entries.map((entry) => (
        <Card key={`${entry.warehouseId}-${entry.clientId}-${entry.uom}`}
          variant="outlined" onClick={(event) => {
            if ((event.target as HTMLElement).closest("button")) return;
            onMobileSelect(entry);
          }} sx={{ cursor: { xs: "pointer", sm: "default" },
            outline: { xs: selected?.warehouseId === entry.warehouseId
              && selected.clientId === entry.clientId && selected.uom === entry.uom
              ? "2px solid" : "none", sm: "none" },
            outlineColor: "primary.main" }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}
              alignItems={{ md: "center" }}>
              <WarehouseOutlined color="secondary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={800}>
                  {entry.warehouseCode} · {entry.warehouseName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ownerLabel(entry.clientCode, entry.clientName, t)}
                  {" · "}{t("stock.total", { quantity: quantity(entry.totalQuantity, locale), uom: entry.uom })}
                  {" · "}{t("stock.counts.items", { count: entry.itemCount })}
                  {" · "}{entry.handlingUnitCount} HU
                  {" · "}{t("stock.counts.locations", { count: entry.locationCount })}
                </Typography>
              </Box>
              <ListRowActions actions={
                <Button variant="outlined" startIcon={<VisibilityOutlined />}
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}
                  onClick={() => onSelect(entry)}>{t("stock.consult")}</Button>
              } status={quantityChips(entry, t, locale)} />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function StockLocationList({
  entries,
  units,
  onSelect,
  onMobileSelect,
  selected
}: {
  entries: StockLocationSummary[];
  units: StockUnit[];
  onSelect: (entry: StockLocationSummary) => void;
  onMobileSelect: (entry: StockLocationSummary) => void;
  selected: StockLocationSummary | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  if (entries.length === 0) {
    return <Alert severity="info">{t("stock.location.none")}</Alert>;
  }
  return (
    <Stack spacing={1.5}>
      {entries.map((entry) => (
        <Card key={`${entry.locationType}-${entry.locationId}-${entry.clientId}-${entry.uom}`}
          variant="outlined" onClick={(event) => {
            if ((event.target as HTMLElement).closest("button")) return;
            onMobileSelect(entry);
          }} sx={{ cursor: { xs: "pointer", sm: "default" },
            outline: { xs: selected?.locationId === entry.locationId
              && selected.clientId === entry.clientId && selected.uom === entry.uom
              ? "2px solid" : "none", sm: "none" },
            outlineColor: "primary.main" }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}
              alignItems={{ md: "center" }}>
              <LocationOnOutlined color="secondary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={800}>
                  {entry.locationCode} · {entry.locationName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {entry.warehouseCode}
                  {entry.areaCode ? ` · ${t("stock.location.zone", { code: entry.areaCode })}` : ""}
                  {entry.sectionCode ? ` · ${t("stock.location.section", { code: entry.sectionCode })}` : ""}
                  {" · "}{ownerLabel(entry.clientCode, entry.clientName, t)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("stock.total", { quantity: quantity(entry.totalQuantity, locale), uom: entry.uom })}
                  {" · "}{t("stock.counts.items", { count: entry.itemCount })}
                  {" · "}{entry.handlingUnitCount} HU
                </Typography>
                <Typography variant="body2" fontWeight={650}>
                  {[...new Map(units.filter((unit) =>
                    unit.locationType === entry.locationType
                    && unit.locationCode === entry.locationCode
                    && unit.clientId === entry.clientId
                    && unit.uom === entry.uom
                  ).map((unit) => [
                    unit.itemId, `${unit.itemCode} · ${unit.itemName}`
                  ])).values()].join(" · ") || t("stock.location.unidentified")}
                </Typography>
              </Box>
              <ListRowActions actions={
                <>
                  <Chip label={(() => {
                  const scoped = units.filter((unit) =>
                    unit.locationType === entry.locationType
                    && unit.locationCode === entry.locationCode
                    && unit.clientId === entry.clientId
                    && unit.uom === entry.uom);
                  const labels = [...new Set(scoped.map((unit) => flowLabel(unit, t)))];
                  return labels.length === 1 ? labels[0] : t("stock.flow.mixed");
                })()} variant="outlined"
                  sx={{ display: { xs: "none", sm: "inline-flex" } }} />
                  <Button variant="outlined" startIcon={<VisibilityOutlined />}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                    onClick={() => onSelect(entry)}>{t("stock.consult")}</Button>
                </>
              } status={entry.locationType === "STORAGE_BIN"
                ? quantityChips(entry, t, locale)
                : <Chip size="small" color="warning" label={t("stock.quality.unavailable")} />} />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function StockHuList({
  groups,
  onSelect,
  onMobileSelect,
  selectedId
}: {
  groups: StockUnit[][];
  onSelect: (units: StockUnit[]) => void;
  onMobileSelect: (units: StockUnit[]) => void;
  selectedId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  if (groups.length === 0) {
    return <Alert severity="info">{t("stock.hu.none")}</Alert>;
  }
  return (
    <Stack spacing={1.5}>
      {groups.map((units) => {
        const first = units[0];
        const total = units.reduce((sum, unit) => sum + unit.quantity, 0);
        const articles = [...new Map(units.map((unit) => [
          unit.itemId, `${unit.itemCode} · ${unit.itemName}`
        ])).values()];
        return (
          <Card key={first.handlingUnitId} variant="outlined"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button")) return;
              onMobileSelect(units);
            }} sx={{ cursor: { xs: "pointer", sm: "default" },
              outline: { xs: selectedId === first.handlingUnitId
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main" }}>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ md: "center" }}>
                <LocalShippingOutlined color="secondary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {first.handlingUnitNumber} · {articles.join(" | ")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quantity(total, locale)} {first.uom}
                    {" · "}{first.warehouseCode} · {first.locationCode}
                    {" · "}{ownerLabel(first.clientCode, first.clientName, t)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("stock.counts.stockUnits", { count: units.length })}
                    {units.some((unit) => unit.lotNumber)
                      ? ` · ${t("stock.hu.lots", { lots: units.map((unit) =>
                        unit.lotNumber).filter(Boolean).join(", ") })}` : ""}
                    {units.some((unit) => unit.serialNumber)
                      ? ` · S/N ${units.map((unit) =>
                        unit.serialNumber).filter(Boolean).join(", ")}` : ""}
                  </Typography>
                </Box>
                <ListRowActions actions={
                  <>
                    <Chip label={flowLabel(first, t)} variant="outlined"
                      sx={{ display: { xs: "none", sm: "inline-flex" } }} />
                    <Button variant="outlined" startIcon={<VisibilityOutlined />}
                      sx={{ display: { xs: "none", sm: "inline-flex" } }}
                      onClick={() => onSelect(units)}>{t("stock.consult")}</Button>
                  </>
                } status={<Chip label={qualityLabel(first.qualityStatus, t)}
                  color={first.qualityStatus === "AVAILABLE"
                    ? "success" : first.qualityStatus === "QUARANTINED"
                      ? "warning" : "error"} />} />
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

function StockHuDialog({
  units,
  onShowMovements,
  onClose
}: {
  units: StockUnit[] | null;
  onShowMovements: (unit: StockUnit) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  return (
    <Dialog open={Boolean(units)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("stock.hu.title", { hu: units?.[0]?.handlingUnitNumber })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {units?.map((unit) => (
            <Card key={unit.inventoryUnitId} variant="outlined">
              <CardContent>
                <Typography fontWeight={800}>
                  {unit.itemCode} · {unit.itemName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {quantity(unit.quantity, locale)} {unit.uom}
                  {" · "}{ownerLabel(unit.clientCode, unit.clientName, t)}
                  {" · "}{unit.warehouseCode} · {unit.locationCode}
                </Typography>
                <Typography variant="body2">
                  {unit.lotNumber ? t("stock.hu.lot", { lot: unit.lotNumber }) : t("stock.hu.noLot")}
                  {unit.serialNumber ? ` · S/N ${unit.serialNumber}` : ""}
                  {unit.expirationDate
                    ? ` · ${t("stock.hu.expiration", { date: new Date(
                      unit.expirationDate).toLocaleDateString(locale) })}` : ""}
                  {` · ${t("stock.hu.rotation", { policy: unit.rotationPolicy })}`}
                </Typography>
                <Button size="small" startIcon={<HistoryOutlined />}
                  onClick={() => onShowMovements(unit)} sx={{ mt: 1 }}>
                  {t("stock.tabs.movement")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("stock.close")}</Button></DialogActions>
    </Dialog>
  );
}

function ScopedStockDetail({
  warehouse,
  location,
  onShowMovements,
  onClose
}: {
  warehouse: StockWarehouseSummary | null;
  location: StockLocationSummary | null;
  onShowMovements: (unit: StockUnit) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const [search, setSearch] = useState("");
  const scope = location ?? warehouse;
  const units = useQuery({
    queryKey: [
      "stock-units-scope", scope?.warehouseId,
      location?.locationType, location?.locationId
    ],
    queryFn: () => loadStockUnitsByScope(
      scope!.warehouseId, location?.locationType, location?.locationId
    ),
    enabled: Boolean(scope)
  });
  const clientId = scope?.clientId ?? null;
  const filtered = useMemo(() => (units.data ?? []).filter((unit) =>
    unit.uom === scope?.uom
    && unit.clientId === clientId
    && (!search.trim() || matchesListSyntax({
      article: `${unit.itemCode} ${unit.itemName}`,
      hu: unit.handlingUnitNumber,
      entrepot: unit.warehouseCode,
      emplacement: `${unit.locationCode} ${unit.locationName}`,
      zone: `${unit.areaCode ?? ""} ${unit.areaName ?? ""}`,
      section: `${unit.sectionCode ?? ""} ${unit.sectionName ?? ""}`,
      lot: unit.lotNumber,
      serie: unit.serialNumber,
      qualite: unit.qualityStatus,
      statut: `${unit.stockStatus} ${unit.handlingUnitStatus}`,
      client: `${unit.clientCode ?? ""} ${unit.clientName ?? t("stock.ownStock")}`
    }, search))
  ), [clientId, scope?.uom, search, units.data, t]);
  const title = location
    ? t("stock.detail.scopeLocation", { location: location.locationCode, uom: location.uom })
    : warehouse
      ? t("stock.detail.scopeWarehouse", { warehouse: warehouse.warehouseCode, uom: warehouse.uom })
      : "";

  return (
    <Dialog open={Boolean(scope)} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            {ownerLabel(scope?.clientCode ?? null, scope?.clientName ?? null, t)}
            {location?.areaCode ? ` · ${t("stock.location.zone", { code: location.areaCode })}` : ""}
            {location?.sectionCode ? ` · ${t("stock.location.section", { code: location.sectionCode })}` : ""}
          </Alert>
          <TextField fullWidth label={t("stock.detail.search")}
            placeholder={'article:MAT0001 hu:HU-000001 qualite:AVAILABLE'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            helperText={t("stock.detail.syntax")}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
          {units.isError && (
            <Alert severity="error">{t("stock.detail.loadError")}</Alert>
          )}
          {!units.isLoading && !units.isError && filtered.length === 0 && (
            <Alert severity="info">{t("stock.detail.noUnit")}</Alert>
          )}
          {filtered.map((unit) => (
            <Card key={unit.inventoryUnitId} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                  alignItems={{ md: "center" }}>
                  <InventoryOutlined color="secondary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={800}>
                      {unit.itemCode} · {unit.itemName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {unit.handlingUnitNumber} · {quantity(unit.quantity, locale)} {unit.uom}
                      {" · "}{unit.warehouseCode} · {unit.locationCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ownerLabel(unit.clientCode, unit.clientName, t)}
                      {unit.lotNumber ? ` · lot ${unit.lotNumber}` : ""}
                      {unit.serialNumber ? ` · S/N ${unit.serialNumber}` : ""}
                      {` · ${t("stock.detail.receipt", { receipt: unit.receiptNumber })}`}
                    </Typography>
                  </Box>
                  <ListRowActions status={
                    <Stack direction="row" spacing={1}>
                      <Chip label={qualityLabel(unit.qualityStatus, t)}
                        color={unit.qualityStatus === "AVAILABLE"
                          ? "success" : unit.qualityStatus === "QUARANTINED"
                            ? "warning" : "error"} />
                      <Chip label={unit.stockStatus === "ON_HAND"
                        ? t("stock.stockStatus.onHand") : unit.stockStatus}
                        color={unit.stockStatus === "ON_HAND"
                          ? "success" : "default"} />
                    </Stack>
                  } actions={
                    <Button variant="outlined" size="small"
                      startIcon={<HistoryOutlined />}
                      onClick={() => onShowMovements(unit)}>
                      {t("stock.tabs.movement")}
                    </Button>
                  } />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("stock.close")}</Button></DialogActions>
    </Dialog>
  );
}

function StockDetail({
  item,
  onClose,
  canCorrectIdentity,
  canCorrectRotation,
  onShowMovements
}: {
  item: StockItemSummary | null;
  onClose: () => void;
  canCorrectIdentity: boolean;
  canCorrectRotation: boolean;
  onShowMovements: (unit: StockUnit) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const [search, setSearch] = useState("");
  const [unitToCorrect, setUnitToCorrect] = useState<StockUnit | null>(null);
  const [correctedDate, setCorrectedDate] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [unitToCorrectSerial, setUnitToCorrectSerial] =
    useState<StockUnit | null>(null);
  const [correctedSerial, setCorrectedSerial] = useState("");
  const [serialCorrectionReason, setSerialCorrectionReason] = useState("");
  const [unitToCorrectLot, setUnitToCorrectLot] =
    useState<StockUnit | null>(null);
  const [correctedLot, setCorrectedLot] = useState("");
  const [correctedExpiration, setCorrectedExpiration] = useState("");
  const [lotCorrectionReason, setLotCorrectionReason] = useState("");
  const units = useQuery({
    queryKey: ["stock-units", item?.itemId],
    queryFn: () => loadStockUnits(item!.itemId),
    enabled: Boolean(item)
  });
  const filtered = useMemo(() => (units.data ?? []).filter((unit) =>
    unit.uom === item?.uom
    && (!search.trim() || matchesListSyntax({
      hu: unit.handlingUnitNumber,
      entrepot: unit.warehouseCode,
      emplacement: `${unit.locationCode} ${unit.locationName}`,
      lot: unit.lotNumber,
      serie: unit.serialNumber,
      qualite: unit.qualityStatus,
      statut: `${unit.stockStatus} ${unit.handlingUnitStatus}`,
      conditionnement: `${unit.packagingCode} ${unit.packagingStatus}`,
      reception: `${unit.receiptNumber} ${unit.receiptSourceType}`
    }, search))
  ), [item?.uom, search, units.data]);
  const correction = useMutation({
    mutationFn: () => correctStockRotationDate(
      unitToCorrect!.inventoryUnitId,
      new Date(correctedDate).toISOString(),
      correctionReason.trim(),
      unitToCorrect!.version
    ),
    onSuccess: async () => {
      setUnitToCorrect(null);
      setCorrectionReason("");
      await units.refetch();
    }
  });
  const serialCorrection = useMutation({
    mutationFn: () => correctStockSerialNumber(
      unitToCorrectSerial!.inventoryUnitId,
      correctedSerial.trim(),
      serialCorrectionReason.trim(),
      unitToCorrectSerial!.version
    ),
    onSuccess: async () => {
      setUnitToCorrectSerial(null);
      setCorrectedSerial("");
      setSerialCorrectionReason("");
      await units.refetch();
    }
  });
  const lotCorrection = useMutation({
    mutationFn: () => correctStockLotTraceability(
      unitToCorrectLot!.inventoryUnitId,
      correctedLot.trim(),
      correctedExpiration || null,
      lotCorrectionReason.trim(),
      unitToCorrectLot!.version
    ),
    onSuccess: async () => {
      setUnitToCorrectLot(null);
      setCorrectedLot("");
      setCorrectedExpiration("");
      setLotCorrectionReason("");
      await units.refetch();
    }
  });
  const nextUnitId = useMemo(() => {
    const eligible = (units.data ?? []).filter((unit) =>
      unit.uom === item?.uom
      && unit.qualityStatus === "AVAILABLE"
      && unit.stockStatus === "ON_HAND"
    );
    if (eligible.length === 0 || eligible[0].rotationPolicy === "NONE") {
      return null;
    }
    return [...eligible].sort((left, right) => {
      const policy = left.rotationPolicy;
      if (policy === "FEFO") {
        const expiryComparison = (left.expirationDate ?? "9999-12-31")
          .localeCompare(right.expirationDate ?? "9999-12-31");
        if (expiryComparison !== 0) return expiryComparison;
      }
      const dateComparison = left.rotationDate.localeCompare(right.rotationDate);
      return policy === "LIFO" ? -dateComparison : dateComparison;
    })[0]?.inventoryUnitId ?? null;
  }, [item?.uom, units.data]);

  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {t("stock.detail.title", { item: item?.itemCode, name: item?.itemName, uom: item?.uom })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField fullWidth label={t("stock.detail.searchHu")}
            placeholder={'hu:HU-000001 emplacement:E001 lot:LOT01'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            helperText={t("stock.detail.huSyntax")}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
          {units.isError && (
            <Alert severity="error">{t("stock.detail.huLoadError")}</Alert>
          )}
          {units.data?.some((unit) =>
            unit.uom === item?.uom
            && unit.rotationPolicy === "FEFO"
            && !unit.expirationDate
          ) && (
            <Alert severity="warning">
              {t("stock.detail.fefoWarning")}
            </Alert>
          )}
          {filtered.length === 0 && !units.isLoading && !units.isError && (
            <Alert severity="info">
              {t("stock.detail.noLogisticUnit")}
            </Alert>
          )}
          {filtered.map((unit) => (
            <Card key={unit.inventoryUnitId} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                  alignItems={{ md: "center" }}>
                  <LocationOnOutlined color="secondary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={800}>
                      {unit.handlingUnitNumber} · {quantity(unit.quantity, locale)}
                      {" "}{unit.uom}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {unit.warehouseCode} · {unit.locationCode}
                      {" · "}{unit.locationName}
                      {unit.packagingCode
                        ? ` · ${t("stock.detail.packaging", { packaging: unit.packagingCode })}`
                        : ` · ${t("stock.detail.packaging", { packaging: unit.packagingStatus })}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("stock.detail.receiptLabel", { receipt: unit.receiptNumber })}
                      {" · "}{unit.receiptSourceType}
                      {unit.lotNumber ? ` · lot ${unit.lotNumber}` : ""}
                      {unit.expirationDate
                        ? ` · ${t("stock.hu.expiration", { date: new Date(
                          unit.expirationDate).toLocaleDateString(locale) })}` : ""}
                      {unit.serialNumber ? ` · S/N ${unit.serialNumber}` : ""}
                    </Typography>
                    <Typography variant="caption" display="block"
                      color="text.secondary">
                      {t("stock.detail.rotationDate", {
                        policy: unit.rotationPolicy,
                        date: new Date(unit.rotationDate).toLocaleString(locale),
                        source: unit.rotationDateSource
                      })}
                    </Typography>
                  </Box>
                  {unit.inventoryUnitId === nextUnitId && (
                    <Chip label={t("stock.detail.nextExit")} color="secondary" />
                  )}
                  <ListRowActions actions={
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small"
                        startIcon={<HistoryOutlined />}
                        onClick={() => onShowMovements(unit)}>
                        {t("stock.tabs.movement")}
                      </Button>
                      {canCorrectRotation && (
                        <Button variant="outlined" size="small"
                          onClick={() => {
                            setUnitToCorrect(unit);
                            setCorrectedDate(
                              new Date(unit.rotationDate).toISOString().slice(0, 16)
                            );
                            setCorrectionReason("");
                          }}>
                          {t("stock.detail.correctDate")}
                        </Button>
                      )}
                      {canCorrectIdentity && unit.serialNumber && (
                        <Button variant="outlined" size="small"
                          onClick={() => {
                            setUnitToCorrectSerial(unit);
                            setCorrectedSerial(unit.serialNumber ?? "");
                            setSerialCorrectionReason("");
                          }}>
                          {t("stock.detail.correctSerial")}
                        </Button>
                      )}
                      {canCorrectIdentity && unit.lotNumber && (
                        <Button variant="outlined" size="small"
                          onClick={() => {
                            setUnitToCorrectLot(unit);
                            setCorrectedLot(unit.lotNumber ?? "");
                            setCorrectedExpiration(
                              unit.expirationDate?.slice(0, 10) ?? ""
                            );
                            setLotCorrectionReason("");
                          }}>
                          {t("stock.detail.correctLot")}
                        </Button>
                      )}
                    </Stack>
                  } status={
                    <Stack direction="row" spacing={1}>
                      <Chip label={qualityLabel(unit.qualityStatus, t)}
                        color={unit.qualityStatus === "AVAILABLE"
                          ? "success" : unit.qualityStatus === "QUARANTINED"
                            ? "warning" : "error"} />
                      <Chip label={unit.stockStatus === "ON_HAND"
                        ? t("stock.stockStatus.onHand") : unit.stockStatus}
                        color={unit.stockStatus === "ON_HAND"
                          ? "success" : "default"} />
                    </Stack>
                  } />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("stock.close")}</Button>
      </DialogActions>
      <Dialog open={Boolean(unitToCorrect)}
        onClose={() => setUnitToCorrect(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("stock.correction.rotationTitle", { hu: unitToCorrect?.handlingUnitNumber })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("stock.correction.rotationWarning")}
            </Alert>
            <TextField type="datetime-local" label={t("stock.correction.newRotationDate")}
              value={correctedDate}
              onChange={(event) => setCorrectedDate(event.target.value)}
              InputLabelProps={{ shrink: true }} />
            <TextField required multiline minRows={3}
              label={t("stock.correction.reason")}
              value={correctionReason}
              onChange={(event) => setCorrectionReason(event.target.value)}
              helperText={t("stock.correction.reasonHint")} />
            {correction.isError && (
              <Alert severity="error">
                {t("stock.correction.reloadError")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitToCorrect(null)}>{t("stock.correction.cancel")}</Button>
          <Button variant="contained"
            disabled={!correctedDate
              || correctionReason.trim().length < 3
              || correction.isPending}
            onClick={() => correction.mutate()}>
            {t("stock.correction.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(unitToCorrectSerial)}
        onClose={() => setUnitToCorrectSerial(null)}
        fullWidth maxWidth="sm">
        <DialogTitle>
          {t("stock.correction.serialTitle", { hu: unitToCorrectSerial?.handlingUnitNumber })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("stock.correction.serialWarning")}
            </Alert>
            <TextField label={t("stock.correction.currentSerial")}
              value={unitToCorrectSerial?.serialNumber ?? ""} disabled />
            <TextField required label={t("stock.correction.newSerial")}
              value={correctedSerial}
              inputProps={{ maxLength: 150 }}
              onChange={(event) => setCorrectedSerial(event.target.value)} />
            <TextField required multiline minRows={3}
              label={t("stock.correction.reason")}
              value={serialCorrectionReason}
              onChange={(event) =>
                setSerialCorrectionReason(event.target.value)}
              helperText={t("stock.correction.reasonHint")} />
            {serialCorrection.isError && (
              <Alert severity="error">
                {serialCorrection.error instanceof Error
                  ? serialCorrection.error.message
                  : t("stock.correction.impossible")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitToCorrectSerial(null)}>{t("stock.correction.cancel")}</Button>
          <Button variant="contained"
            disabled={!correctedSerial.trim()
              || correctedSerial.trim() === unitToCorrectSerial?.serialNumber
              || serialCorrectionReason.trim().length < 3
              || serialCorrection.isPending}
            onClick={() => serialCorrection.mutate()}>
            {t("stock.correction.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(unitToCorrectLot)}
        onClose={() => setUnitToCorrectLot(null)}
        fullWidth maxWidth="sm">
        <DialogTitle>
          {t("stock.correction.lotTitle", { hu: unitToCorrectLot?.handlingUnitNumber })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("stock.correction.lotWarning")}
            </Alert>
            <TextField required label={t("stock.correction.lotNumber")}
              value={correctedLot}
              inputProps={{ maxLength: 100 }}
              onChange={(event) => setCorrectedLot(event.target.value)} />
            <TextField type="date" label={t("stock.correction.expirationDate")}
              value={correctedExpiration}
              onChange={(event) => setCorrectedExpiration(event.target.value)}
              InputLabelProps={{ shrink: true }} />
            <TextField required multiline minRows={3}
              label={t("stock.correction.reason")}
              value={lotCorrectionReason}
              onChange={(event) => setLotCorrectionReason(event.target.value)}
              helperText={t("stock.correction.reasonHint")} />
            {lotCorrection.isError && (
              <Alert severity="error">
                {lotCorrection.error instanceof Error
                  ? lotCorrection.error.message
                  : t("stock.correction.impossible")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitToCorrectLot(null)}>{t("stock.correction.cancel")}</Button>
          <Button variant="contained"
            disabled={!correctedLot.trim()
              || (correctedLot.trim() === unitToCorrectLot?.lotNumber
                && correctedExpiration
                  === (unitToCorrectLot?.expirationDate?.slice(0, 10) ?? ""))
              || lotCorrectionReason.trim().length < 3
              || lotCorrection.isPending}
            onClick={() => lotCorrection.mutate()}>
            {t("stock.correction.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
